import React, {useCallback, useEffect} from 'react';
import {
  Flex,
  FormControl,
  Text,
  Button,
  Input,
  FormLabel,
  Textarea,
} from '@chakra-ui/react';
import {ChakraClose} from '../../Icons/Carbon';
import {AiCategoryFieldDefinitionConfig} from '../../../shared/fields/enrichment';
import CreatableSelect from 'react-select/creatable';

export const CategoriesEditor = ({
  config,
  onConfigChange,
  isDisabled = false,
}: {
  config: AiCategoryFieldDefinitionConfig;
  onConfigChange: (config: AiCategoryFieldDefinitionConfig) => void;
  isDisabled?: boolean;
}) => {
  const addEmptyCategory = useCallback(
    () =>
      onConfigChange({
        ...config,
        categories: [
          ...config.categories,
          {
            name: '',
            definition: '',
            examples: [],
          },
        ],
      }),
    [config, onConfigChange]
  );

  useEffect(() => {
    if (!config.categories.length) {
      addEmptyCategory();
    }
  }, [addEmptyCategory, config.categories.length]);

  return (
    <Flex direction="column" gap="4">
      <Flex direction="column" gap="2">
        {config.categories.map((_, idx) => (
          <CategoryEditor
            key={idx}
            categoryIndex={idx}
            config={config}
            onConfigChange={onConfigChange}
            isDisabled={isDisabled}
          />
        ))}
      </Flex>

      {config.categories.length < 10 && !isDisabled && (
        <Button w="fit-content" fontSize="sm" onClick={addEmptyCategory}>
          + Add category
        </Button>
      )}
    </Flex>
  );
};

const CategoryEditor = ({
  categoryIndex,
  config,
  onConfigChange,
  isDisabled = false,
}: {
  categoryIndex: number;
  config: AiCategoryFieldDefinitionConfig;
  onConfigChange: (config: AiCategoryFieldDefinitionConfig) => void;
  isDisabled?: boolean;
}) => {
  const category = config.categories[categoryIndex];

  const updateCategory = ({
    name,
    definition,
    examples,
  }: Partial<{
    name: string;
    definition: string;
    examples: string[];
  }>) => {
    onConfigChange({
      ...config,
      categories: config.categories.map((category, idx) =>
        idx === categoryIndex
          ? {
              ...category,
              ...(name !== undefined && {name}),
              ...(definition !== undefined && {definition}),
              ...(examples !== undefined && {examples}),
            }
          : category
      ),
    });
  };

  const removeCategory = () => {
    onConfigChange({
      ...config,
      categories: config.categories.filter((_, idx) => idx !== categoryIndex),
    });
  };

  return (
    <Flex
      border="1px solid"
      borderColor="kgray.200"
      borderRadius="md"
      direction="column"
      position="relative"
      gap="4"
      p="4"
      pt="3"
    >
      {config.categories.length > 1 && !isDisabled && (
        <Button
          onClick={removeCategory}
          position="absolute"
          px="0"
          right="1"
          top="1"
          variant="ghost"
        >
          <ChakraClose boxSize="5" />
        </Button>
      )}

      <Text fontSize="md">Category {categoryIndex + 1}</Text>

      <Flex direction="column" gap="2">
        <FormControl>
          <FormLabel fontSize="xs" textTransform="uppercase">
            Category Name
          </FormLabel>
          <Input
            isDisabled={isDisabled}
            onChange={(e) => updateCategory({name: e.target.value})}
            placeholder="Short descriptive name"
            value={category.name}
          />
        </FormControl>
        <FormControl mt={2}>
          <FormLabel fontSize="xs" textTransform="uppercase">
            Examples (optional)
          </FormLabel>
          <CreatableSelect
            formatCreateLabel={(input) => `Add "${input}"`}
            isDisabled={isDisabled}
            isMulti
            noOptionsMessage={() => 'Start typing to add examples'}
            onChange={(e) => updateCategory({examples: e.map((o) => o.value)})}
            placeholder="2-5 example companies"
            value={category.examples.map((example) => ({
              label: example,
              value: example,
            }))}
          />
        </FormControl>
        <FormControl mt={2}>
          <FormLabel fontSize="xs" textTransform="uppercase">
            Category Definition
          </FormLabel>
          <Textarea
            isDisabled={isDisabled}
            onChange={(e) => updateCategory({definition: e.target.value})}
            value={category.definition}
          />
        </FormControl>
      </Flex>
    </Flex>
  );
};
